import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { 
  Package, ArrowLeft, DollarSign, Calendar, MapPin, 
  Phone, Mail, MessageSquare, Upload, HelpCircle,
  CheckCircle, Clock, Truck, FileText, User, Building, Shield
} from 'lucide-react';
import axios from 'axios';
import { 
  maskClientName, 
  maskCompanyName, 
  maskPhone, 
  maskCEP,
  getClientDisplayData
} from '../utils/dataProtection';
import { 
  applyCurrencyMask, 
  parseCurrency, 
  handleCurrencyInput 
} from '../utils/currencyMask';

interface Pedido {
  id: number;
  cliente: {
    nome: string;
    empresa: string;
    email: string;
    telefone: string;
    cep: string;
  };
  tipo_equipamento: string;
  marca: string;
  modelo: string;
  tipo_peca: string;
  descricao: string;
  data_criacao: string;
  prazo: string;
  valor_estimado?: number;
}

interface OrcamentoForm {
  valor: number;
  condicao_peca: string;
  garantia: string;
  tipo_entrega: string;
  tempo_envio: string;
  frete_incluso: boolean;
  observacoes: string;
  arquivo?: File;
}

const EnviarOrcamento: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [pedido, setPedido] = useState<Pedido | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [valorMasked, setValorMasked] = useState('R$ 0,00');
  
  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm<OrcamentoForm>();
  const freteIncluso = watch('frete_incluso');

  useEffect(() => {
    loadPedido();
  }, [id]);

  const loadPedido = async () => {
    try {
      // Simular carregamento de dados
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const mockPedido: Pedido = {
        id: parseInt(id || '1'),
        cliente: {
          nome: 'João Silva',
          empresa: 'Empresa ABC Ltda',
          email: 'joao@empresaabc.com',
          telefone: '(11) 99999-9999',
          cep: '01234-567'
        },
        tipo_equipamento: 'Escavadeira',
        marca: 'Caterpillar',
        modelo: '320D',
        tipo_peca: 'Motor',
        descricao: 'Motor completo para escavadeira Caterpillar 320D. Inclui pistões, cilindros, cabeçote e sistema de injeção. Peça original ou equivalente de qualidade.',
        data_criacao: '2024-01-15',
        prazo: '2024-01-20',
        valor_estimado: 15000
      };
      
      setPedido(mockPedido);
    } catch (error) {
      toast.error('Erro ao carregar detalhes do pedido');
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data: OrcamentoForm) => {
    setIsSubmitting(true);
    try {
      // Validar valor mínimo
      const valorNumerico = parseCurrency(valorMasked);
      if (valorNumerico <= 0) {
        toast.error('O valor deve ser maior que zero');
        setIsSubmitting(false);
        return;
      }
      
      // Simular envio do orçamento
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      toast.success('Orçamento enviado com sucesso!');
      navigate('/fornecedor/dashboard');
    } catch (error) {
      toast.error('Erro ao enviar orçamento');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getClientDisplayInfo = () => {
    // Na página de envio de orçamento, sempre mascarar os dados do cliente
    if (!pedido) return null;
    return getClientDisplayData(pedido.cliente, true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando detalhes do pedido...</p>
        </div>
      </div>
    );
  }

  if (!pedido) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Package className="w-12 h-12 text-red mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Pedido não encontrado</h2>
          <p className="text-gray-600 mb-4">O pedido solicitado não foi encontrado.</p>
          <button
            onClick={() => navigate('/fornecedor/dashboard')}
            className="btn btn-primary"
          >
            Voltar ao Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <img 
                src="/logo-pecaja.png" 
                alt="PeçaJá & Sobratema" 
                className="h-12 w-auto object-contain"
              />
            </div>

            <button
              onClick={() => navigate('/fornecedor/dashboard')}
              className="btn btn-outline"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar ao Dashboard
            </button>
          </div>
        </div>
      </header>

      <div className="container py-8">
        {/* Breadcrumb */}
        <div className="breadcrumb">
          <button
            onClick={() => navigate('/fornecedor/dashboard')}
            className="breadcrumb-item"
          >
            Dashboard
          </button>
          <span className="breadcrumb-separator">/</span>
          <button
            onClick={() => navigate(`/fornecedor/pedidos/${id}`)}
            className="breadcrumb-item"
          >
            Pedido #{id}
          </button>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">Enviar Orçamento</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2">
            <div className="card">
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Enviar Orçamento
                </h1>
                <p className="text-gray-600">
                  Preencha os detalhes do seu orçamento para o pedido #{pedido.id}
                </p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Valor */}
                <div>
                  <label className="form-label flex items-center">
                    Valor do Orçamento *
                    <div className="relative ml-2 group">
                      <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10 pointer-events-none">
                        Valor total do orçamento em reais (R$)
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  </label>
                  <div className="flex items-center space-x-2">
                    <DollarSign className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    <input
                      type="text"
                      value={valorMasked}
                      onChange={(e) => {
                        const maskedValue = applyCurrencyMask(e.target.value);
                        setValorMasked(maskedValue);
                        setValue('valor', parseCurrency(maskedValue));
                      }}
                      className="form-input flex-1"
                      placeholder="R$ 0,00"
                    />
                  </div>
                  {errors.valor && (
                    <p className="text-red text-sm mt-1">{errors.valor.message}</p>
                  )}
                </div>

                {/* Condição da Peça */}
                <div>
                  <label className="form-label flex items-center">
                    Condição da Peça *
                    <div className="relative ml-2 group">
                      <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10 pointer-events-none">
                        Estado atual da peça (nova, usada, recondicionada, etc.)
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  </label>
                  <select
                    {...register('condicao_peca', { required: 'Condição da peça é obrigatória' })}
                    className="form-select"
                  >
                    <option value="">Selecione...</option>
                    <option value="nova">Nova</option>
                    <option value="recondicionada">Recondicionada</option>
                    <option value="usada">Usada</option>
                    <option value="equivalente">Equivalente</option>
                  </select>
                  {errors.condicao_peca && (
                    <p className="text-red text-sm mt-1">{errors.condicao_peca.message}</p>
                  )}
                </div>

                {/* Garantia */}
                <div>
                  <label className="form-label flex items-center">
                    Garantia *
                    <div className="relative ml-2 group">
                      <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10 pointer-events-none">
                        Período de garantia oferecido para a peça
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  </label>
                  <select
                    {...register('garantia', { required: 'Garantia é obrigatória' })}
                    className="form-select"
                  >
                    <option value="">Selecione...</option>
                    <option value="sem_garantia">Sem Garantia</option>
                    <option value="3_meses">3 Meses</option>
                    <option value="6_meses">6 Meses</option>
                    <option value="12_meses">12 Meses</option>
                    <option value="18_meses">18 Meses</option>
                    <option value="24_meses">24 Meses</option>
                  </select>
                  {errors.garantia && (
                    <p className="text-red text-sm mt-1">{errors.garantia.message}</p>
                  )}
                </div>

                {/* Tipo de Entrega */}
                <div>
                  <label className="form-label flex items-center">
                    Tipo de Entrega *
                    <div className="relative ml-2 group">
                      <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10 pointer-events-none">
                        Informe se a peça será enviada ou deve ser retirada no local
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  </label>
                  <select
                    {...register('tipo_entrega', { required: 'Tipo de entrega é obrigatório' })}
                    className="form-select"
                  >
                    <option value="">Selecione...</option>
                    <option value="envio">Envio para o cliente</option>
                    <option value="retirada">Retirada no local</option>
                    <option value="ambos">Ambas as opções disponíveis</option>
                  </select>
                  {errors.tipo_entrega && (
                    <p className="text-red text-sm mt-1">{errors.tipo_entrega.message}</p>
                  )}
                </div>

                {/* Tempo de Envio */}
                <div>
                  <label className="form-label flex items-center">
                    Tempo de Envio *
                    <div className="relative ml-2 group">
                      <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10 pointer-events-none">
                        Tempo necessário para enviar a peça para o cliente ou ela estar disponível para ser retirada
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  </label>
                  <select
                    {...register('tempo_envio', { required: 'Tempo de envio é obrigatório' })}
                    className="form-select"
                  >
                    <option value="">Selecione...</option>
                    <option value="imediato">Imediato</option>
                    <option value="1_3_dias">1-3 dias úteis</option>
                    <option value="3_5_dias">3-5 dias úteis</option>
                    <option value="5_7_dias">5-7 dias úteis</option>
                    <option value="7_10_dias">7-10 dias úteis</option>
                    <option value="10_15_dias">10-15 dias úteis</option>
                    <option value="15_30_dias">15-30 dias úteis</option>
                  </select>
                  {errors.tempo_envio && (
                    <p className="text-red text-sm mt-1">{errors.tempo_envio.message}</p>
                  )}
                </div>

                {/* Frete */}
                <div>
                  <label className="form-label">Frete</label>
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        {...register('frete_incluso')}
                        className="h-4 w-4 text-red focus:ring-red border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Frete Incluso</span>
                    </label>
                  </div>
                </div>

                {/* Observações */}
                <div>
                  <label className="form-label flex items-center">
                    Observações
                    <div className="relative ml-2 group">
                      <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10 pointer-events-none">
                        Informações adicionais sobre a peça, condições especiais, etc.
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  </label>
                  <textarea
                    {...register('observacoes')}
                    className="form-textarea"
                    rows={4}
                    placeholder="Exemplo: Peça original Caterpillar, compatível com modelos 320D e 320DL. Inclui pistões, cilindros e cabeçote. Testada e funcionando perfeitamente. Aceitamos PIX, boleto ou cartão. Entrega em todo Brasil. Garantia de 6 meses. Frete incluso para SP/RJ. Para outras regiões, consulte valor do frete."
                  />
                </div>

                {/* Arquivo */}
                <div>
                  <label className="form-label">Anexar Proposta (Opcional)</label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                    <div className="space-y-1 text-center">
                      <Upload className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600">
                        <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-red hover:text-red/80">
                          <span>Fazer upload de um arquivo</span>
                          <input
                            id="file-upload"
                            name="file-upload"
                            type="file"
                            className="sr-only"
                            accept=".pdf,.doc,.docx"
                            onChange={handleFileChange}
                          />
                        </label>
                        <p className="pl-1">ou arraste e solte</p>
                      </div>
                      <p className="text-xs text-gray-500">PDF, DOC até 10MB</p>
                    </div>
                  </div>
                  {selectedFile && (
                    <div className="mt-2 flex items-center space-x-2">
                      <FileText className="w-4 h-4 text-green-500" />
                      <span className="text-sm text-gray-600">{selectedFile.name}</span>
                    </div>
                  )}
                </div>

                {/* Submit */}
                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={() => navigate('/fornecedor/dashboard')}
                    className="btn btn-outline flex-1"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn btn-primary flex-1"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center justify-center">
                        <div className="spinner mr-3"></div>
                        Enviando...
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Enviar Orçamento
                      </div>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Pedido Info */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Detalhes do Pedido</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">
                    {pedido.tipo_equipamento} {pedido.marca} {pedido.modelo}
                  </h4>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Peça:</span> {pedido.tipo_peca}
                  </p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-600 mb-2">{pedido.descricao}</p>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span>Prazo: {new Date(pedido.prazo).toLocaleDateString('pt-BR')}</span>
                  </div>
                  {pedido.valor_estimado && (
                    <div className="flex items-center space-x-2">
                      <DollarSign className="w-4 h-4 text-gray-400" />
                      <span>Estimativa: {formatCurrency(pedido.valor_estimado)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Cliente Info */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Cliente</h3>
              <div className="space-y-3">
                {(() => {
                  const clientInfo = getClientDisplayInfo();
                  if (!clientInfo) return null;
                  
                  return (
                    <>
                      <div className="flex items-center space-x-3">
                        <User className="w-4 h-4 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-500">Nome</p>
                          <p className="font-medium text-gray-900">{clientInfo.nome}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Building className="w-4 h-4 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-500">Empresa</p>
                          <p className="font-medium text-gray-900">{clientInfo.empresa}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-500">CEP</p>
                          <p className="font-medium text-gray-900">{clientInfo.cep}</p>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Tips */}
            <div className="card bg-blue-50 border-blue-200">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">Dicas para um Orçamento Melhor</h3>
              <div className="space-y-2 text-sm text-blue-800">
                <div className="flex items-start space-x-2">
                  <Shield className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Seja específico sobre a qualidade da peça</span>
                </div>
                <div className="flex items-start space-x-2">
                  <Truck className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Considere incluir o frete no valor</span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Ofereça garantia quando possível</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnviarOrcamento; 